import LockIcon from "@/assets/dark_gradient/lock.svg?react";
import {Tooltip, type PlacesType} from "@/components/Common/Tooltip";
import {useSelectedCompany} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {AbilityContext} from "@/permissions";
import {useContext, useId, type FC, type ReactNode} from "react";
import {Trans, useTranslation} from "react-i18next";

const GenerativeAiLock: FC<{
  badgeClassName?: string;
  children: ReactNode;
  className?: string;
  locked: boolean;
  place?: PlacesType;
  showBadge?: boolean;
}> = ({badgeClassName, children, className, locked, place = "bottom", showBadge = true}) => {
  const {t} = useTranslation("common");
  const id = useId();
  const ability = useContext(AbilityContext);
  const canMailSupport = ability.can("create", "MailToSupport");
  const selectedCompany = useSelectedCompany();
  const tooltipId = `generative-ai-lock-${id}`;

  if (!locked) {
    return <>{children}</>;
  }

  return (
    <div className={clsxm("relative", className)}>
      <div className="absolute inset-0 z-20 cursor-not-allowed" data-tooltip-id={tooltipId} />
      <Tooltip
        id={tooltipId}
        place={place}
        variant="light"
        className="border-neutral-white-dark z-50 rounded-lg! border p-3! shadow-md"
        classNameArrow="border-neutral-white-dark border-r border-b"
        clickable
        opacity={1}>
        <span className="text-neutral-grey-darkest block w-56 text-left text-xs leading-relaxed">
          <Trans
            t={t}
            i18nKey="generative_ai_disabled_tooltip"
            components={{
              contact: canMailSupport ? (
                <a
                  className="text-primary-dark-solid hover:text-primary-medium-solid cursor-pointer font-semibold no-underline transition-colors duration-150"
                  href={`mailto:sales@refluenced.com?subject=${t("enable_generative_ai")} - ${selectedCompany?.company_name}`}
                />
              ) : (
                <span />
              ),
            }}
          />
        </span>
      </Tooltip>
      {showBadge && (
        <div
          className={clsxm(
            "pointer-events-none absolute top-2 right-2 z-30 flex size-6 items-center justify-center rounded-full bg-white/90 shadow-sm",
            badgeClassName,
          )}>
          <LockIcon className="size-3 shrink-0" />
        </div>
      )}
      <div className="pointer-events-none h-full opacity-60 grayscale select-none">{children}</div>
    </div>
  );
};

export default GenerativeAiLock;
